import { useContext, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import { createEvaluationQuestion } from '../../api'
import styles from './styles.module.css'

interface EvaluationModalProps {
  isOpen: boolean
  onClose: () => void
  onSaveComplete: () => void
}

function EvaluationModal({ isOpen, onClose, onSaveComplete }: EvaluationModalProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const [query, setQuery] = useState('')
  const [expectedAllowed, setExpectedAllowed] = useState(true)
  const [expectedAnswer, setExpectedAnswer] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  function resetForm() {
    setQuery('')
    setExpectedAllowed(true)
    setExpectedAnswer('')
  }

  function handleClose() {
    if (isSaving) return
    resetForm()
    onClose()
  }

  async function handleSave() {
    if (!query.trim()) return

    setIsSaving(true)

    try {
      await fetchData(
        createEvaluationQuestion({
          query: query.trim(),
          expectedAllowed,
          expectedAnswer: expectedAnswer.trim() || undefined,
        }),
      )
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Evaluation question added.',
        severity: 'success',
      })
      resetForm()
      onSaveComplete()
      onClose()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to add evaluation question.',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box className={styles.modalContainer}>
        <div className={styles.title}>Add Evaluation Question</div>

        <div className={styles.formContainer}>
          <TextField
            label='Question'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={3}
            disabled={isSaving}
          />

          <TextField
            label='Expected Result'
            value={expectedAllowed ? 'allow' : 'block'}
            onChange={(e) => setExpectedAllowed(e.target.value === 'allow')}
            fullWidth
            size='small'
            select
            disabled={isSaving}
          >
            <MenuItem value='allow'>Should Allow</MenuItem>
            <MenuItem value='block'>Should Block</MenuItem>
          </TextField>

          <TextField
            label='Expected Answer (optional, for future use)'
            value={expectedAnswer}
            onChange={(e) => setExpectedAnswer(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
            disabled={isSaving}
          />
        </div>

        <div className={styles.buttonContainer}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={handleClose}
            disabled={isSaving}
          />
          <PrimaryButton
            buttonName='Add'
            onClick={handleSave}
            disabled={!query.trim() || isSaving}
          />
        </div>
      </Box>
    </Modal>
  )
}

export default EvaluationModal
